import './styles/app.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { ToastProvider, toastManager } from '@/components/ui/toast'
import { useSessionBroadcast } from '@/lib/session'
import { createAppRouter } from './router'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
    mutations: { retry: false },
  },
})

const router = createAppRouter(queryClient)

function SessionBroadcast() {
  useSessionBroadcast()
  return null
}

async function enableMocks() {
  if (import.meta.env.MODE === 'integration') {
    if ('serviceWorker' in navigator) {
      const isMockWorker = (worker: ServiceWorker | null) => {
        if (!worker) return false
        const url = new URL(worker.scriptURL)
        return url.origin === location.origin && url.pathname === '/mockServiceWorker.js'
      }
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(
        registrations
          .filter((registration) =>
            [registration.active, registration.waiting, registration.installing].some(isMockWorker),
          )
          .map((registration) => registration.unregister()),
      )
      // Unregistering does not release this document from an existing MSW controller.
      if (isMockWorker(navigator.serviceWorker.controller)) {
        location.reload()
        return false
      }
    }
    return true
  }
  if (!(import.meta.env.DEV || import.meta.env.MODE === 'mock')) return true
  const { worker } = await import('./mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass', quiet: true })
  return true
}

enableMocks().then((ready) => {
  if (!ready) return
  const container = document.getElementById('root') as (HTMLElement & { __root?: Root }) | null
  if (!container) return
  container.__root ??= createRoot(container)
  container.__root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider toastManager={toastManager}>
          <SessionBroadcast />
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
})
